import { Delegate } from '../utility/Delegate';
import { DragType } from './DragType';
import { EmbeddedCustomCursor, ICustomCursor } from './EmbeddedCustomCursor';
import { IAvatar } from './IAvatar';
import { MiscUtil } from '../utility/MiscUtil';
import { NormalizedEvent } from '../event/NormalizedEvent';
import { TypeUtil } from '../utility/TypeUtil';

interface DragInstancePresModelType {
  readonly dragInstanceType: string;
}

class DragInstance {
  private IsCopyDrag: boolean;
  protected draggedObject: object;
  private readonly DragAvatar: IAvatar;
  private IsShiftDrag: boolean;
  public readonly presmodelType: string;

  public constructor(dragged: object, dragAvatar: IAvatar) {
    this.draggedObject = dragged;
    this.DragAvatar = dragAvatar;
    this.IsCopyDrag = false;
    this.IsShiftDrag = false;
    this.isRightDrag = false;

    // Check if the payload contains a dragpresmodel
    if (!MiscUtil.IsNullOrUndefined(dragged) && TypeUtil.HasField(dragged, 'dragInstanceType')) {
      this.presmodelType = (dragged as DragInstancePresModelType).dragInstanceType;
    }
  }

  /**
   * Optional.  Set if want to do custom handling on Ctrl or Cmd key state changed.
   * Action that will be executed if IsCopyDrag property of the drag instance is changed
   */
  public ctrlStateChanged: Delegate<() => void> = new Delegate<() => void>();
  public add_ctrlStateChanged(ev: () => void): void {
    this.ctrlStateChanged.add(ev);
  }
  public remove_ctrlStateChanged(ev: () => void): void {
    this.ctrlStateChanged.remove(ev);
  }

  /**
   * Optional.  Set if want to do custom handling on Shift key state changed.
   * Action that will be executed if IsShiftDrag property of the drag instance is changed
   */
  public shiftStateChanged: Delegate<() => void> = new Delegate<() => void>();
  public add_shiftStateChanged(ev: () => void): void {
    this.shiftStateChanged.add(ev);
  }
  public remove_shiftStateChanged(ev: () => void): void {
    this.shiftStateChanged.remove(ev);
  }

  private get payload(): object {
    return this.draggedObject;
  }

  private get hasPayload(): boolean {
    return !MiscUtil.IsNullOrUndefined(this.draggedObject);
  }

  public get isCopyDrag(): boolean {
    return this.IsCopyDrag;
  }

  public set isCopyDrag(value: boolean) {
    if (this.IsCopyDrag !== value) {
      this.IsCopyDrag = value;
      if (!MiscUtil.IsNullOrUndefined(this.ctrlStateChanged)) {
        this.ctrlStateChanged.invoke();
      }
    }
  }

  public get isShiftDrag(): boolean {
    return this.IsShiftDrag;
  }

  public set isShiftDrag(value: boolean) {
    if (this.IsShiftDrag !== value) {
      this.IsShiftDrag = value;
      if (!MiscUtil.IsNullOrUndefined(this.shiftStateChanged)) {
        this.shiftStateChanged.invoke();
      }
    }
  }

  public isRightDrag: boolean;

  private get dragAvatar(): IAvatar {
    return this.DragAvatar;
  }

  private get dragType(): DragType {
    return DragType.dragdrop;
  }

  private get readyToDrop(): boolean {
    return true;
  }

  private get defaultDropCursor(): ICustomCursor {
    return EmbeddedCustomCursor.noOp;
  }

  /*virtual*/ public moveTo(e: NormalizedEvent): void {/* */ }
  /*virtual*/ public endDragAt(e: NormalizedEvent): void {/* */ }
  /*virtual*/ public cancelDrag(): void {/* */ }

  /**
   * @param value
   * Checks the payload to see if the object being dragged is the given object.
   * @returns true if testObject is the draggedObject or draggedObject is an array of one item, and
   * that item is testObject
   */
  public payloadEquals(value: object): boolean {
    if (this.draggedObject === value) {
      return true;
    }
    if (Array.isArray(this.draggedObject)) {
      const draggedObjects: object[] = this.draggedObject as object[];
      if (draggedObjects.length === 1
        && draggedObjects[0] === value) {
        return true;
      }
    }
    return false;
  }

  /**
   * Action that will be executed if the drag instance is not dropped on a drop target
   * or is dropped on a drop target that refuses the drop
   */
  /*virtual*/ public dropToNowhereAction(): void {/* */ }

  // redirection for saltarelle generated javascript getters
  /*virtual*/ public get_payload(): object { return this.payload; }
  /*virtual*/ public get_hasPayload(): boolean { return this.hasPayload; }
  /*virtual*/ public get_dragAvatar(): IAvatar { return this.dragAvatar; }
  /*virtual*/ public get_dragType(): DragType { return this.dragType; }
  /*virtual*/ public get_readyToDrop(): boolean { return this.readyToDrop; }
  /*virtual*/ public get_defaultDropCursor(): ICustomCursor { return this.defaultDropCursor; }
}

export { DragInstance };
